import React from 'react';
import { useRouter } from 'next/router';
import { LOGIN_PAGE, ROOT_PAGE, SIGNUP_PAGE } from 'constants/pages';
import ArrowLeft from 'svg/ArrowLeft';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonLink } from 'components/Button';
import Modal from 'components/modals/Modal';

/** Why the user can't create the VAIR event they picked. */
export type VairAccessReason = 'signed-out' | 'not-connected' | 'not-authorized';

export type VairAccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  reason: VairAccessReason;
  /** Rating sessions are VAIR Pro only; other VAIR events also allow Ambassadors. */
  isRatingSession?: boolean;
  /** Starts the VAIR OAuth redirect (from useVairConnection). */
  onConnect: () => void;
  /** Reopens the event-type modal the user came from. */
  onGoBack: () => void;
};

// Shown when someone picks a VAIR event type they can't create. It takes both a
// connected VAIR account and the right VAIR role, so the copy names the missing
// half and offers the matching fix (log in / connect / nothing but ask VAIR).
const VairAccessModal: React.FC<VairAccessModalProps> = ({
  isOpen,
  onClose,
  reason,
  isRatingSession,
  onConnect,
  onGoBack,
}) => {
  const router = useRouter();
  const nextParam = encodeURIComponent(router.asPath || ROOT_PAGE);
  const eventLabel = isRatingSession ? 'VAIR rating session' : 'VAIR event';
  const roleLabel = isRatingSession ? 'VAIR Pros' : 'VAIR Ambassadors and VAIR Pros';

  const heading =
    reason === 'signed-out'
      ? `Log in to run a ${eventLabel}`
      : reason === 'not-connected'
      ? 'Connect your VAIR account'
      : `${eventLabel}s are for ${roleLabel}`;

  const body =
    reason === 'signed-out'
      ? `Running a ${eventLabel} takes a Bounce account with a connected VAIR account, plus ${roleLabel} status on VAIR.`
      : reason === 'not-connected'
      ? `Running a ${eventLabel} takes a connected VAIR account with ${roleLabel} status. Connect your VAIR account and we'll check your status.`
      : `Your VAIR account is connected, but running a ${eventLabel} is reserved for ${roleLabel}. Check your status in VAIR — if you think this is a mistake, reach out to VAIR.`;

  return (
    <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="md:max-w-[25rem] xs:max-w-md">
      <div className="flex flex-col gap-6 px-6 pb-5 pt-6">
        <div className="flex flex-row items-start justify-between gap-4">
          <img src="/images/vair/vair-black.png" alt="VAIR" className="h-5 w-auto object-contain" />
          <button
            className="p-0.5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {heading}
          </h3>
          <p className="typography-product-body font-normal text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {body}
          </p>
        </div>

        <div className="flex w-full flex-col gap-3">
          {reason === 'signed-out' && (
            <>
              <ButtonLink
                href={`${LOGIN_PAGE}?next=${nextParam}`}
                variant="primary"
                size="lg"
                className="w-full"
              >
                Log in
              </ButtonLink>
              <ButtonLink
                href={`${SIGNUP_PAGE}?next=${nextParam}`}
                variant="secondary"
                size="lg"
                className="w-full"
              >
                Sign up free
              </ButtonLink>
            </>
          )}

          {reason === 'not-connected' && (
            <Button variant="primary" size="lg" className="w-full" onClick={onConnect}>
              Connect my VAIR
            </Button>
          )}

          {reason === 'not-authorized' && (
            <Button variant="primary" size="lg" className="w-full" onClick={onClose}>
              Got it
            </Button>
          )}

          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={onGoBack}
            iconLeft={<ArrowLeft className="h-4 w-4" />}
          >
            Go back
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default VairAccessModal;
